import i18n from "../../../locales/i18n";
import {
  firstImageOfListingTemplateInput,
  includeBsadInput,
  listingPrice,
  mainListingInput,
  mainTemplateColorInput,
  realtorContactInfoTemplateInput,
  realtorImageTemplateInput,
  includeRealtorImageInput,
  realtorLogoTemplateInput,
  realtorNameTemplateInput,
  realtorPhoneNumberTemplateInput,
  secondImageOfListingTemplateInput,
  getBsadPrompt,
  getRealtorImagePrompt,
  getVerticalFooterPrompt,
  getVisualStylePrompt,
  getColorInfo,
  getInputImageLabel,
  hasRealtorImage,
  promptGenerationFinalParams,
  templateDataType,
  promptGeneratorInitialParams
} from "../template_sections";

export const listing_promo_var_1: templateDataType = {
  templateName: i18n.t("creativesGenerator.templates.listingPromo.templateName"),
  displayImageUrl: "/static/creative-template-display-images/real-estate-listing-promo-1.png",
  description: i18n.t("creativesGenerator.templates.listingPromo.description"),
  inputs: [
    includeBsadInput,
    mainTemplateColorInput,
    mainListingInput,
    realtorNameTemplateInput,
    realtorPhoneNumberTemplateInput,
    realtorImageTemplateInput,
    includeRealtorImageInput,
    realtorLogoTemplateInput,
    firstImageOfListingTemplateInput,
    secondImageOfListingTemplateInput,
    listingPrice,
    {
      label: i18n.t("creativesGenerator.templates.listingPromo.topText"),
      type: "text",
      nameInPrompt: "top_text_1",
      showInSection: "template_specific",
      description: i18n.t("creativesGenerator.templates.listingPromo.topTextDescription"),
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        return i18n.t("creativesGenerator.templates.listingPromo.topTextDefaultValue", {
          city: mainListing?.address?.city ?? "_____"
        });
      }
    },
    {
      label: i18n.t("creativesGenerator.templates.listingPromo.textWithBackground"),
      type: "text",
      nameInPrompt: "text_with_background",
      showInSection: "template_specific",
      description: i18n.t(
        "creativesGenerator.templates.listingPromo.textWithBackgroundDescription"
      ),
      defaultValue: (params: promptGeneratorInitialParams) =>
        i18n.t("creativesGenerator.templates.listingPromo.textWithBackgroundDefaultValue")
    },
    {
      label: i18n.t("creativesGenerator.templates.listingPromo.interestingPropertyInfo"),
      type: "text",
      nameInPrompt: "interesting_property_info",
      showInSection: "template_specific",
      description: i18n.t(
        "creativesGenerator.templates.listingPromo.interestingPropertyInfoDescription"
      ),
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        return i18n.t(
          "creativesGenerator.templates.listingPromo.interestingPropertyInfoDefaultValue",
          {
            roomsCount: mainListing?.roomsCount ?? "____",
            neighborhood: mainListing?.address?.neighborhood ?? "____"
          }
        );
      }
    }
  ],
  promptGenerator: (params: promptGenerationFinalParams) => {
    const { mainColorValue, lighterColorValue, darkerColorValue } = getColorInfo(params);
    const textParams = params.templateFinalInputs.filter((input) => input.type === "text");

    return `
{
  "description": "Real Estate Advertisement Flyer",
  "project_context": {
    "type": "Real Estate Social Media Graphic",
    "tone": "Professional, vibrant, energetic, and successful",
    "aspect_ratio": "1:1 (Square)"
  },
  ${getVisualStylePrompt(params)},
  ${getBsadPrompt(params)}

  "layout": {
    "type": "vertical_split",
    "left_column": {
      "width": "50%",
      "content": [
        {
          "type": "image",
          "source": "listing_image",
          "style": {
            "height": "45%",
            "object_fit": "cover"
          }
        },
        {
          "type": "image",
          "source": "listing_image_2",
          "style": {
            "height": "45%",
            "object_fit": "cover"
          }
        }
      ]
    },
    "right_column": {
      "width": "50%",
      "background_color": "${darkerColorValue}",
      "background_style": "gradient from ${mainColorValue} to ${darkerColorValue}",
      "content": [
        {
          "type": "text_block",
          "headline": {
            "text": "${
              textParams.find((param) => param.nameInPrompt === "top_text_1")?.finalValue
            }",
            "font_color": "${lighterColorValue}",
            "font_size": "large",
            "font_weight": "bold"
          },
          "sub_text": [
            {
              "text": "${
                textParams.find((param) => param.nameInPrompt === "text_with_background")
                  ?.finalValue
              }",
              "font_color": "#ffffff",
              "background_color": "${darkerColorValue}",
              "font_size": "medium",
            },
            {
              "text": "${
                textParams.find((param) => param.nameInPrompt === "interesting_property_info")
                  ?.finalValue
              }",
              "font_color": "#ffffff",
              "font_size": "medium"
            }
          ]
        },
        {
          "type": "price_box",
          "price": "${
            textParams.find((param) => param.nameInPrompt === "listing_price")?.finalValue
          }",
          "background_color": "${lighterColorValue}",
          "text_color": "#ffffff",
          "font_size": "extra_large",
          "additional_details": "Large, bold white numbers on a vibrant ${mainColorValue} color, rounded left edges rectangle background",
          "style": {
            "rounded_corners": true,
            "margin_top": "20px"
          }
        }
      ]
    }
  },
  ${
    hasRealtorImage(params)
      ? `${getRealtorImagePrompt(
          params,
          "The realtor should overlap the footer slightly to create depth."
        )},`
      : ""
  }
  ${getVerticalFooterPrompt(params)},
  "input_images": {
    "listing_image": "${getInputImageLabel("listing_image")}",
    "listing_image_2": "${getInputImageLabel("listing_image_2")}",
    ${hasRealtorImage(params) ? `"realtor_image": "${getInputImageLabel("realtor_image")}",` : ""}
    "realtor_logo": "${getInputImageLabel("realtor_logo")}"
  },
  important_notes: [
    "Ensure the final image is vibrant and eye-catching to stand out on social media feeds",
    "Maintain a professional yet celebratory and catchy tone suitable for real estate marketing",
    "DO NOT alter the provided images beyond removing backgrounds and resizing/cropping as necessary for composition"
  ]
}`;
  }
};
